import React, { useCallback, useEffect, useId, useRef, useState, Children, isValidElement } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import mermaid from 'mermaid';
import { useAuth } from '../context/AuthContext';
import AdminNav from './AdminNav';

const DOCS_LOCALE_KEY = 'docsLocale';

function MermaidBlock({ code }) {
  const reactId = useId().replace(/:/g, '');
  const containerRef = useRef(null);
  const seqRef = useRef(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !code || !String(code).trim()) return undefined;

    let cancelled = false;
    seqRef.current += 1;
    const renderId = `mermaid-${reactId}-${seqRef.current}`;

    mermaid
      .render(renderId, String(code).trim())
      .then(({ svg }) => {
        if (!cancelled && container) {
          container.innerHTML = svg;
        }
      })
      .catch(() => {
        if (!cancelled && container) {
          container.textContent = '';
        }
      });

    return () => {
      cancelled = true;
    };
  }, [code, reactId]);

  return <div className="my-4 overflow-x-auto doc-mermaid rounded-lg border border-gray-200 bg-white p-4" ref={containerRef} />;
}

const markdownComponents = {
  h1: (props) => <h1 className="text-2xl font-bold mt-8 mb-4 text-gray-900 first:mt-0" {...props} />,
  h2: (props) => <h2 className="text-xl font-semibold mt-8 mb-3 text-gray-900" {...props} />,
  h3: (props) => <h3 className="text-lg font-semibold mt-6 mb-2 text-gray-800" {...props} />,
  p: (props) => <p className="text-gray-700 leading-relaxed mb-4" {...props} />,
  ul: (props) => <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-1" {...props} />,
  ol: (props) => <ol className="list-decimal pl-6 mb-4 text-gray-700 space-y-1" {...props} />,
  li: (props) => <li className="leading-relaxed" {...props} />,
  a: (props) => (
    <a className="text-blue-600 hover:text-blue-800 underline" target="_blank" rel="noopener noreferrer" {...props} />
  ),
  blockquote: (props) => (
    <blockquote className="border-l-4 border-gray-300 pl-4 italic text-gray-600 my-4" {...props} />
  ),
  table: (props) => (
    <div className="overflow-x-auto my-4">
      <table className="min-w-full border border-gray-200 text-sm" {...props} />
    </div>
  ),
  thead: (props) => <thead className="bg-gray-50" {...props} />,
  th: (props) => <th className="border border-gray-200 px-3 py-2 text-left font-semibold text-gray-800" {...props} />,
  td: (props) => <td className="border border-gray-200 px-3 py-2 text-gray-700" {...props} />,
  hr: (props) => <hr className="my-8 border-gray-200" {...props} />,
  code: ({ className, children, ...props }) => {
    const match = /language-(\w+)/.exec(className || '');
    const language = match?.[1];
    const codeString = String(children).replace(/\n$/, '');
    if (language === 'mermaid') {
      return <MermaidBlock code={codeString} />;
    }
    return (
      <code className="bg-gray-100 text-red-800 px-1.5 py-0.5 rounded text-sm font-mono" {...props}>
        {children}
      </code>
    );
  },
  pre: ({ children }) => {
    try {
      const child = Children.only(children);
      if (isValidElement(child) && String(child.props.className || '').includes('language-mermaid')) {
        return <>{children}</>;
      }
    } catch (_) {
      /* multiple children */
    }
    return <pre className="bg-gray-50 p-4 rounded-lg overflow-x-auto text-sm my-4 border border-gray-200">{children}</pre>;
  }
};

function readStoredLocale() {
  try {
    const v = localStorage.getItem(DOCS_LOCALE_KEY);
    if (v === 'en' || v === 'de') return v;
  } catch (_) {
    /* ignore */
  }
  return 'en';
}

const AdminDocumentation = () => {
  const { t } = useTranslation();
  const { getAuthHeaders, logout } = useAuth();
  const [locale, setLocale] = useState(readStoredLocale);
  const [items, setItems] = useState([]);
  const [slug, setSlug] = useState('index.md');
  const [markdown, setMarkdown] = useState('');
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [error, setError] = useState(null);

  const persistLocale = useCallback((next) => {
    setLocale(next);
    try {
      localStorage.setItem(DOCS_LOCALE_KEY, next);
    } catch (_) {
      /* ignore */
    }
  }, []);

  const fetchManifest = useCallback(async () => {
    setLoadingList(true);
    setError(null);
    try {
      const res = await fetch(`/api/admin/documentation/manifest?locale=${encodeURIComponent(locale)}`, {
        headers: getAuthHeaders()
      });
      if (res.status === 401) {
        logout();
        return;
      }
      if (!res.ok) {
        setItems([]);
        setError(t('admin.documentation.errorLoadManifest'));
        return;
      }
      const data = await res.json();
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      setItems([]);
      setError(t('admin.documentation.errorLoadManifest'));
    } finally {
      setLoadingList(false);
    }
  }, [getAuthHeaders, locale, logout, t]);

  const fetchDoc = useCallback(
    async (pathSlug) => {
      setLoadingDoc(true);
      setError(null);
      try {
        const q = new URLSearchParams({ locale, path: pathSlug });
        const res = await fetch(`/api/admin/documentation/file?${q.toString()}`, {
          headers: getAuthHeaders()
        });
        if (res.status === 401) {
          logout();
          return;
        }
        if (!res.ok) {
          setMarkdown('');
          setError(t('admin.documentation.errorLoadPage'));
          return;
        }
        const text = await res.text();
        setMarkdown(text);
      } catch (e) {
        setMarkdown('');
        setError(t('admin.documentation.errorLoadPage'));
      } finally {
        setLoadingDoc(false);
      }
    },
    [getAuthHeaders, locale, logout, t]
  );

  useEffect(() => {
    mermaid.initialize({
      startOnLoad: false,
      securityLevel: 'strict',
      theme: 'neutral'
    });
  }, []);

  useEffect(() => {
    fetchManifest();
  }, [fetchManifest]);

  useEffect(() => {
    if (slug) {
      fetchDoc(slug);
    }
  }, [slug, fetchDoc]);

  useEffect(() => {
    if (!items.length) return;
    const exists = items.some((i) => i.slug === slug);
    if (!exists) {
      const first = items[0]?.slug;
      if (first) setSlug(first);
    }
  }, [items, slug]);

  return (
    <div className="max-w-7xl mx-auto p-6">
      <AdminNav />
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('admin.documentation.title')}</h1>
            <p className="text-gray-600 text-sm mt-1">{t('admin.documentation.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">{t('admin.documentation.language')}</span>
            <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
              <button
                type="button"
                onClick={() => persistLocale('en')}
                className={`px-3 py-1.5 text-sm font-medium ${locale === 'en' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              >
                EN
              </button>
              <button
                type="button"
                onClick={() => persistLocale('de')}
                className={`px-3 py-1.5 text-sm font-medium border-l border-gray-200 ${locale === 'de' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              >
                DE
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-lg bg-red-50 text-red-800 px-4 py-3 text-sm" role="alert">
            {error}
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-8">
          <nav
            className="lg:w-64 shrink-0 border border-gray-200 rounded-lg p-3 max-h-[70vh] overflow-y-auto"
            aria-label={t('admin.documentation.toc')}
          >
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 px-2">
              {t('admin.documentation.toc')}
            </h2>
            {loadingList ? (
              <p className="text-sm text-gray-500 px-2">{t('admin.documentation.loading')}</p>
            ) : (
              <ul className="space-y-1">
                {items.map((item) => (
                  <li key={item.slug}>
                    <button
                      type="button"
                      onClick={() => setSlug(item.slug)}
                      className={`w-full text-left px-2 py-2 rounded-md text-sm ${
                        slug === item.slug ? 'bg-blue-50 text-blue-800 font-medium' : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {item.title}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </nav>

          <div className="flex-1 min-w-0 border border-gray-200 rounded-lg p-6 max-h-[70vh] overflow-y-auto doc-markdown">
            {loadingDoc ? (
              <p className="text-gray-500 text-sm">{t('admin.documentation.loading')}</p>
            ) : (
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                {markdown}
              </ReactMarkdown>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDocumentation;
